import { useMemo } from "react";
import { useSelector } from "react-redux";
import { HotelsShopSelectors } from "@b2bportal/core-hotels";
import { Funnel } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useGetCurrentFunnel } from "@overrides/utilities";
import {
  type HotelShopBookingSectionProps,
  getHasDiscount,
  useAvailabilityHotelPrice,
} from "@tenant/hotels";
import { useGetPriceDisplay } from "@tenant/ui";
import { AccountSelectors } from "@/modules/account";
import { getPercentOff } from "@/utilities/hotels/discount";

export const useHotelShopBookingSection = ({
  hotel,
  goToNextStep,
}: HotelShopBookingSectionProps) => {
  const { t, formatFiatCurrency } = useI18nContext();
  const currFunnel = useGetCurrentFunnel();
  const isInPackagesFlow = currFunnel === Funnel.Package;
  const { getPriceDisplay } = useGetPriceDisplay();
  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);

  const { data: roomProduct, isLoading } = useSelector(
    HotelsShopSelectors.getCheapestRoomProduct
  );

  const totalNights = useSelector(HotelsShopSelectors.getTotalNights);
  const roomCount = useSelector(HotelsShopSelectors.getRoomsCount);
  const perNightPrice = formatFiatCurrency(roomProduct?.perNightTotal.fiat);

  const { isLoading: isRoomsLoading } = useSelector(
    HotelsShopSelectors.getRooms
  );

  const availabilityPricing = useAvailabilityHotelPrice(
    hotel,
    totalNights,
    roomCount
  );

  // Compute display prices from the cheapest room product (shop API).
  // Falls back to availability-level pricing while rooms are still loading.
  // Follows the same discount + points pattern as useHotelRoomNightlyPricing.
  const roomProductPricing = useMemo(() => {
    if (!roomProduct) return null;

    // Step 1: Determine if points pricing is available for this account
    const accountSpecificPrice =
      rewardsAccountId != null
        ? roomProduct.tripTotal.accountSpecific?.[rewardsAccountId]
        : undefined;
    const showPointsPricing =
      rewardsAccountId != null &&
      accountSpecificPrice != null &&
      Object.keys(accountSpecificPrice).length > 0;

    // Step 2: Format nightly price (use discounted price when available)
    const nightlyDA = roomProduct.nightlyDiscountAwareTaxesAndFeesIncluded;
    const nightlyDiscounted =
      nightlyDA.priceWithAllDiscounts ?? nightlyDA.priceWithUnmanagedDiscounts;
    const hasNightlyDiscount = getHasDiscount(nightlyDA);
    const { priceText: nightlyPrice, rewardsText: nightlyPoints } =
      getPriceDisplay({
        prices: hasNightlyDiscount
          ? nightlyDiscounted
          : nightlyDA.priceWithNoDiscounts,
      });

    // Step 3: Format total price + strikethrough for discounts
    const totalDA = roomProduct.totalDiscountAwareTaxesAndFeesIncluded;
    const totalDiscounted = totalDA
      ? (totalDA.priceWithAllDiscounts ?? totalDA.priceWithUnmanagedDiscounts)
      : undefined;
    const hasTotalDiscount = totalDA ? getHasDiscount(totalDA) : false;
    const { priceText: totalPrice, rewardsText: totalPoints } = getPriceDisplay(
      {
        prices:
          hasTotalDiscount && totalDiscounted
            ? totalDiscounted
            : (totalDA?.priceWithNoDiscounts ?? roomProduct.tripTotal),
      }
    );

    const totalNotDiscounted =
      hasTotalDiscount && totalDA
        ? getPriceDisplay({ prices: totalDA.priceWithNoDiscounts })
        : { priceText: null, rewardsText: null };
    const originalTotal = showPointsPricing
      ? (totalNotDiscounted.rewardsText ?? totalNotDiscounted.priceText)
      : totalNotDiscounted.priceText;
    const currentTotal = showPointsPricing ? totalPoints : totalPrice;
    const totalPriceNotDiscounted =
      originalTotal && originalTotal !== currentTotal ? originalTotal : null;

    const percentOff = getPercentOff(totalDA);

    return {
      showPointsPricing,
      totalPoints,
      totalPrice,
      totalPriceNotDiscounted,
      percentOff,
      pricePerNight: {
        final: showPointsPricing
          ? t(
              "core-hotels.availabilityPage.results.hotelCard.price.pointsPerNight",
              { points: nightlyPoints, count: roomCount ?? 1 }
            )
          : t(
              "core-hotels.availabilityPage.results.hotelCard.price.pricePerNight",
              { price: nightlyPrice, count: roomCount ?? 1 }
            ),
      },
    };
  }, [roomProduct, rewardsAccountId, getPriceDisplay, t, roomCount]);

  const {
    showPointsPricing,
    totalPoints,
    totalPrice,
    totalPriceNotDiscounted,
    pricePerNight,
    percentOff,
  } = roomProductPricing ?? {
    showPointsPricing: availabilityPricing?.context.showPointsPricing ?? false,
    totalPoints: availabilityPricing?.context.totalPoints ?? "",
    totalPrice: availabilityPricing?.context.totalPrice ?? "",
    totalPriceNotDiscounted:
      availabilityPricing?.text.totalPriceNotDiscounted ?? null,
    pricePerNight: availabilityPricing?.text.pricePerNight ?? { final: "" },
    percentOff: availabilityPricing?.context.percentOff ?? 0,
  };

  const isAvailable = useMemo(() => {
    return !isLoading && roomProduct != null;
  }, [isLoading, roomProduct]);

  return useMemo(() => {
    if (isLoading) {
      return {
        isLoading,
      };
    }
    return {
      isLoading: isLoading,
      isAvailable: isAvailable,
      hotel,
      nextStep: isRoomsLoading || !isAvailable ? null : goToNextStep,
      text: {
        roomCTA: isRoomsLoading
          ? t("loading")
          : t("core-hotels.shop.price.roomCTA"),
        pricePerNight,
        helperText: undefined,
      },
      unavailable: t("core-hotels.shop.price.unavailable"),
      originalPrice: perNightPrice,
      startingAt: t("core-hotels.shop.price.startingAt"),

      totalPrice,
      totalPriceNotDiscounted,
      showPointsPricing,
      totalPoints,
      totalNights,
      rooms: roomCount,
      percentOff,
      isShopSearchVisible: !isInPackagesFlow,
    };
  }, [
    isLoading,
    isAvailable,
    hotel,
    isRoomsLoading,
    goToNextStep,
    t,
    pricePerNight,
    perNightPrice,
    totalPrice,
    totalPriceNotDiscounted,
    showPointsPricing,
    totalPoints,
    totalNights,
    roomCount,
    percentOff,
    isInPackagesFlow,
  ]);
};
